import React, { useEffect, useState } from "react";
import ResultsAtAGlance from "../components/ResultsAtAGlance";
import ScenarioVariablesPanel from "../components/ScenarioVariablesPanel";
import WaterfallChart from "../components/WaterfallChart";
import ActivityChart from "../components/ActivityChart";
import { useSettings } from "../contexts/SettingsContext";

// Screen 1 — Dashboard, arranged to match Pixel Dave's illustration.
// Layout goals:
//   • Top 3×2 grid — all three columns share the *same* total height so
//     the bottom edges of Donut+Elements, Results+Variables, Waterfall+Gauge
//     line up on a single horizontal.
//   • Reserve Activity chart absorbs the remaining vertical space so it never
//     clips against the viewport.
//   • Whole Dashboard fits inside the app-shell without scrolling.
// Top-row columns: RESULTS gains X px (even, scaled from a 1920-px screen); side panels each lose X/2.
const resultsColumns = (settings) => {
  const gap = Number(settings.dashboard_panel_gap_px) || 27;
  const px = Math.max(0, Math.min(198, Math.round((Number(settings.dashboard_results_extra_px) || 0) / 2) * 2));
  const base = `((100% - ${2 * gap}px) / 3)`;
  if (!px) return undefined;
  const x = `(${px} * 100vw / 1920)`;
  return `calc(${base} - ${x} / 2) calc(${base} + ${x}) calc(${base} - ${x} / 2)`;
};


export default function DashboardScreen({
  calc,
  onPlanYearChanged,
  association,
  variables,
  originalVariables,
  onVariableChange,
  onUndo,
  onSave,
  onOpenHistory,
  scenarioCount,
  activeScenarioName,
  activeScenarioTitle,
  onNameActive,
  horizon,
}) {
  const { settings } = useSettings();

  // 2026-03-04 (Dave) — Per-tier widget visibility. Each dashboard
  // widget has a paired `dash_<name>_basic|pro` platform-settings key;
  // when the switch matching this association's tier is off, the widget
  // renders `null` in its grid slot so Basic clients see a lighter
  // home screen. All checks default to visible (settings not yet loaded
  // or key missing → show), matching the TopBar tab-gate contract.
  const tier = ((association && association.tier) || "pro").toLowerCase();
  const suffix = tier === "basic" ? "basic" : "pro";
  const showWidget = (name) => {
    if (!settings) return true;
    const key = `dash_${name}_${suffix}`;
    return Number(settings[key] ?? 1) === 1;
  };
  const showWaterfall    = showWidget("waterfall");
  const showActivity     = showWidget("activity");

  // 2026-02-23 (Dave · Option A) — Dashboard load choreography.
  //
  // First-mount detection: initialize state from the data-ready check
  // so a warm re-mount (user navigating back from Elements / Projections
  // etc.) skips the spinner + settle veil entirely and renders at full
  // opacity immediately. Only a cold mount (association just switched
  // and data is loading) triggers the spinner-then-fade sequence.
  // Without this, a warm re-mount blinked off (spinner flash) then
  // on (fade-in).
  const dataReady = !!(calc && association && variables);
  const [mounted, setMounted] = useState(dataReady);
  const [faded, setFaded] = useState(dataReady);
  useEffect(() => {
    if (!dataReady) {
      // Association swap or logout — reset back to the spinner state so
      // the next reveal fades in cleanly.
      if (mounted || faded) {
        setMounted(false);
        setFaded(false);
      }
      return undefined;
    }
    // Data just became ready but we haven't mounted yet → run the
    // settle-window + fade.
    if (!mounted) {
      setMounted(true);
      const t = setTimeout(() => setFaded(true), 480);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [dataReady]);

  if (!mounted) {
    return (
      <div
        className="flex items-center justify-center h-full text-slate"
        data-testid="dashboard-loading"
      >
        <div className="flex items-center gap-2 text-[12px] tracking-wide">
          <span
            className="inline-block w-3.5 h-3.5 rounded-full animate-spin"
            style={{
              border: "3px solid #0F2A44",
              borderTopColor: "transparent",
            }}
            aria-hidden="true"
          />
          Loading Dashboard&hellip;
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        opacity: faded ? 1 : 0,
        transition: "opacity 220ms ease-out",
        // 2026-02-25 (Dave) — Restore the vertical rhythm broken by
        // commit 71169fb. Before that commit DashboardScreen returned
        // a fragment whose children were direct flex-siblings of the
        // main-content wrapper (which supplies `gap: var(--u-panel-gap)`).
        // Wrapping the fragment in this fade-<div> collapsed the three
        // rows into a single flex-child, so the parent's gap no longer
        // spaced them apart. Making the fade-wrap itself a flex column
        // with the same gap restores the visual gutter between the top
        // 3-panel grid, the Activity chart, and the SOP link — without
        // pushing anything off the viewport.
        display: "flex",
        flexDirection: "column",
        gap: "var(--u-panel-gap)",
        flex: 1,
        minHeight: 0,
        // Top padding = panel gap (main already adds 6px). Dave 2026-10-12.
        paddingTop: "calc(var(--u-panel-gap) - 2px)", // +4px (Dave 2026-10-12)
        paddingBottom: 17, // SOP link removed; chart bottom sits 1/3 closer to canvas edge
      }}
      data-testid="dashboard-fade-wrap"
    >
    <>
      {/* Top grid: equal-height rows so the three columns' bottoms align.
          Step 12 (2026-08-03) — heights now flow via clamp() tokens on
          --u-top-row-min-h + --u-activity-min-h instead of the old
          `flex: 4 1 0 / flex: 3 1 0` recipe (which required a bounded
          parent and collapsed on iPad). Fluid between 1024-1920, snapped
          at 1024/1280/1600 via CSS variables.
          2026-02-15 (Dave) — dropped the `minmax(var(--u-top-row-min-h),
          1fr)` floor and switched to plain `1fr` so the grid rows share
          whatever height the flex parent gives them. The old floor
          forced 2×260 = 520 px min for the top-grid alone and combined
          with the fixed activity height pushed content past the 100vh
          lock, creating the Chrome/Safari scroll-bar bug. */}
      <div
        className="grid grid-cols-3"
        data-testid="dashboard-top-grid"
        style={{
          // Second row is empty since the layout review — collapse it so the
          // activity chart can grow upward (Dave 2026-10-12).
          columnGap: `${Number(settings.dashboard_panel_gap_px) || 27}px`,
          gridTemplateColumns: resultsColumns(settings),
          rowGap: 0,
          gridTemplateRows: "minmax(0, 1fr) 0px",
          flex: "1 1 auto",
          minHeight: 0,
        }}
      >
        <ScenarioVariablesPanel
          variables={variables}
          originalVariables={originalVariables}
          onChange={onVariableChange}
          onUndo={onUndo}
          onSave={onSave}
          activeScenarioName={activeScenarioName}
          scenarioCount={scenarioCount}
          onOpenHistory={onOpenHistory}
        />
        <div
          className="row-span-2 grid"
          data-testid="dashboard-center-col"
          style={{ gridTemplateRows: "minmax(0, 1fr) 0px", minHeight: 0 }}
        >
          <ResultsAtAGlance
            calc={calc}
            association={association}
            variables={variables}
            scenarioCount={scenarioCount}
            activeScenarioName={activeScenarioName}
            activeScenarioTitle={activeScenarioTitle}
            onNameActive={onNameActive}
            onOpenHistory={onOpenHistory}
            onPlanYearChanged={onPlanYearChanged}
          />
          {/* Scenario Variables moved to the top-left slot (Dave 2026-10-12). */}
          <div data-testid="dashboard-center-lower-slot" />
        </div>
        {showWaterfall && <WaterfallChart calc={calc} horizon={horizon} association={association} onBalanceSaved={onPlanYearChanged} />}

        {/* Funding Mix donut moved to the Illustrations tab (Dave 2026-10-12);
            empty placeholder keeps every other panel in its grid slot. */}
        <div data-testid="dashboard-funding-mix-slot" />
        {/* Funding Level panel moved to the Illustrations tab (Dave 2026-10-12). */}
        <div data-testid="dashboard-funding-level-slot" />
      </div>

      {/* Reserve Activity — fluid height so it never collapses on iPad
          landscape and gets extra room on large notebooks. Uses explicit
          `height` (not just min-height) so the panel-compact child's
          `height: 100%` computes properly and Recharts renders bars. */}
      {showActivity && (
        <div
          style={{
            // +50% (Dave 2026-10-12), then +15% more (Oct 2026); bottom stays anchored, grows upward.
            // Capped so the top panels never shrink below their 170px fit (Oct 2026).
            height: `min(calc(var(--u-activity-min-h) * ${1.725 * (Number(settings.dashboard_chart_height_pct) || 80) / 100} + 12px), calc(100dvh - ${339 + (Number(settings.dashboard_chart_gap_px) || 24) - 20 + 2 * (Number(settings.dashboard_edge_tb_px ?? 6) - 6)}px))`,
            display: "flex",
            flexDirection: "column",
            flex: "0 0 auto",
            // Space above chart = fade-wrap gap + this margin (Platform Setting).
            marginTop: `calc(${Number(settings.dashboard_chart_gap_px) || 24}px - var(--u-panel-gap))`,
          }}
          data-testid="dashboard-activity-slot"
        >
          <ActivityChart calc={calc} variables={variables} horizon={horizon} association={association} />
        </div>
      )}

    </>
    </div>
  );
}
